import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Store, Globe2, Layers, Timer, Building2, Route } from "lucide-react";

const METRICS = [
  { value: "20+", label: "Dark Stores Managed", icon: Store },
  { value: "10+", label: "Cities Supported", icon: Globe2 },
  { value: "Multi", label: "QC Platform Partners", icon: Layers },
  { value: "SLA", label: "Driven Execution", icon: Timer },
];

function VisualPanel() {
  return (
    <div className="relative min-h-[480px] lg:min-h-[540px] overflow-hidden rounded-[2rem] border border-white/10 bg-white/5 p-5 lg:p-6 shadow-2xl backdrop-blur-xl">
      <div
        className="absolute inset-0 opacity-50"
        style={{ background: "radial-gradient(circle at 75% 18%, rgba(153,204,0,0.32), transparent 36%)" }}
      />
      <div className="relative grid h-full gap-4">
        {/* Live Operations */}
        <div className="rounded-3xl border border-white/10 bg-black/25 p-5">
          <div className="mb-4 flex items-center justify-between">
            <div className="font-display font-black text-white text-sm lg:text-base">Live Operations View</div>
            <div className="rounded-full bg-[#99CC00] px-3 py-1 text-[11px] font-black text-[#1F1F1F]">ACTIVE</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: "Picking", v: "98%" },
              { k: "Packing", v: "96%" },
              { k: "Dispatch", v: "99%" },
            ].map((x) => (
              <div key={x.k} className="rounded-2xl bg-white/[0.07] p-4">
                <div className="text-xs text-white/55">{x.k}</div>
                <div className="mt-2 text-2xl font-display font-black text-white">{x.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Two stat cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="rounded-3xl border border-white/10 bg-black/30 p-5">
            <Building2 className="mb-3 h-7 w-7 text-[#99CC00]" />
            <div className="font-display font-black text-white text-lg">Dark Store</div>
            <div className="mt-2 text-sm leading-6 text-white/60">Manpower, SOPs, supervision and daily control.</div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-black/30 p-5">
            <Route className="mb-3 h-7 w-7 text-[#99CC00]" />
            <div className="font-display font-black text-white text-lg">City Network</div>
            <div className="mt-2 text-sm leading-6 text-white/60">Route-led supply chain and fulfilment movement.</div>
          </div>
        </div>

        {/* Supply chain mini flow */}
        <div className="relative min-h-[170px] rounded-3xl border border-white/10 bg-black/30 p-5 overflow-hidden">
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 500 220" fill="none" preserveAspectRatio="none" aria-hidden>
            <path
              d="M50 160 C140 60 220 185 310 86 S420 100 465 40"
              stroke="#99CC00" strokeWidth="3" className="oh-route-line" fill="none" opacity="0.85"
            />
            {[
              [70, 145], [180, 105], [300, 120], [430, 70],
            ].map(([x, y], i) => (
              <g key={i}>
                <circle cx={x} cy={y} r="22" stroke="#99CC00" opacity="0.22" fill="none" />
                <circle cx={x} cy={y} r="9" fill="#99CC00" />
              </g>
            ))}
          </svg>
          <div className="relative z-10 font-display font-black text-white text-lg">Supply Chain Flow</div>
          <div className="relative z-10 mt-1 text-sm text-white/60">Warehouse → Dark Store → Dispatch → Customer</div>
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  return (
    <section data-testid="hero" className="relative overflow-hidden bg-[#1F1F1F] px-5 pt-32 pb-20 lg:pt-40 lg:pb-28">
      <div
        className="absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(circle at 85% 15%, rgba(153,204,0,0.32), transparent 30%), radial-gradient(circle at 10% 80%, rgba(153,204,0,0.14), transparent 38%)",
        }}
      />
      <div className="absolute inset-0 oh-grid-dark opacity-40" />

      <div className="relative mx-auto max-w-7xl grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="oh-label-pill" data-testid="hero-eyebrow">Quick Commerce Expansion & Operations Partner</div>
          <h1 className="mt-7 max-w-5xl text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-display font-black text-white leading-[0.95] tracking-tight">
            Powering <span className="text-[#99CC00]">Quick Commerce</span> Expansion
          </h1>
          <p className="mt-7 max-w-2xl text-lg lg:text-xl leading-8 text-white/75">
            We launch, manage and scale dark store, marketplace, warehousing, supply chain and fulfilment operations for QC platforms and fast-growing brands.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-base lg:text-lg font-black text-white">
            <CheckCircle2 className="w-5 h-5 text-[#99CC00]" />
            Managing <span className="text-[#99CC00]">20+</span> dark stores across multiple QC partners.
          </div>

          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="hero-cta-partner"
              className="inline-flex items-center gap-2 rounded-full bg-[#99CC00] text-[#1F1F1F] px-7 py-4 text-sm font-black hover:bg-[#B5E000] transition-colors"
            >
              Partner With OneHill <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-explore"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-7 py-4 text-sm font-bold hover:border-[#99CC00] hover:text-[#99CC00] transition-colors"
            >
              Explore Capabilities
            </Link>
          </div>

          {/* Metrics */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl">
            {METRICS.map((m, i) => (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.2 + i * 0.06 }}
                data-testid={`hero-metric-${i}`}
                className="rounded-3xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl"
              >
                <m.icon className="mb-3 h-6 w-6 text-[#99CC00]" />
                <div className="text-3xl font-display font-black text-white">{m.value}</div>
                <div className="mt-1 text-sm text-white/60">{m.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 28 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
        >
          <VisualPanel />
        </motion.div>
      </div>
    </section>
  );
}
